/* ==========================================================================
   Quick Order — Standalone IIFE
   No build step. Served as-is from theme/assets.
   ========================================================================== */

(function () {
  'use strict';

  // -----------------------------------------------------------------------
  // Selectors
  // -----------------------------------------------------------------------

  var SELECTORS = {
    section: '[data-section-type="quick-order"]',
    row: '[data-qo-row]',
    variantSelect: '[data-qo-variant-select]',
    qty: '[data-qo-qty]',
    qtyDecrement: '[data-qo-qty-decrement]',
    qtyIncrement: '[data-qo-qty-increment]',
    price: '[data-qo-price]',
    lineTotal: '[data-qo-line-total]',
    thumb: '[data-qo-thumb]',
    group: '[data-qo-group]',
    table: '[data-qo-table]',
    footer: '[data-qo-footer]',
    footerCount: '[data-qo-footer-count]',
    footerTotal: '[data-qo-footer-total]',
    submitBtn: '[data-qo-submit]',
    clearBtn: '[data-qo-clear]',
    errorBanner: '[data-qo-error-banner]',
    successBanner: '[data-qo-success-banner]',
    liveRegion: '[data-qo-live-region]'
  };

  // -----------------------------------------------------------------------
  // Money formatting (mirrors theme.js formatMoney)
  // -----------------------------------------------------------------------

  var moneyFormat = (window.theme && window.theme.moneyFormat) || '${{amount}}';

  function formatMoney(cents) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    cents = parseInt(cents, 10) || 0;

    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var match = moneyFormat.match(placeholderRegex);
    if (!match) return cents;

    var value = '';
    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2, ',', '.');
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2, ',', '.');
    }

    return moneyFormat.replace(placeholderRegex, value);
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    if (isNaN(number) || number == null) return '0';
    number = (number / 100.0).toFixed(precision);
    var parts = number.split('.');
    var dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
    var cents = parts[1] ? decimal + parts[1] : '';
    return dollars + cents;
  }

  // -----------------------------------------------------------------------
  // Init
  // -----------------------------------------------------------------------

  var section = document.querySelector(SELECTORS.section);
  if (!section) return;

  // Only initialize interactivity for the member view (has submit button)
  var submitBtn = section.querySelector(SELECTORS.submitBtn);
  if (!submitBtn) return;

  var clearBtn = section.querySelector(SELECTORS.clearBtn);
  var footerCount = section.querySelector(SELECTORS.footerCount);
  var footerTotal = section.querySelector(SELECTORS.footerTotal);
  var errorBanner = section.querySelector(SELECTORS.errorBanner);
  var successBanner = section.querySelector(SELECTORS.successBanner);
  var liveRegion = section.querySelector(SELECTORS.liveRegion);
  var allRows = section.querySelectorAll(SELECTORS.row);
  var allGroups = section.querySelectorAll(SELECTORS.group);

  // -----------------------------------------------------------------------
  // Variant Change
  // -----------------------------------------------------------------------

  function handleVariantChange(row) {
    var select = row.querySelector(SELECTORS.variantSelect);
    if (!select) return;

    var option = select.options[select.selectedIndex];
    var price = option.getAttribute('data-price');
    var available = option.getAttribute('data-available') === 'true';
    var image = option.getAttribute('data-image');

    // Update row data attributes
    row.setAttribute('data-variant-id', select.value);
    row.setAttribute('data-price', price);
    row.setAttribute('data-available', String(available));

    // Update price display
    var priceCell = row.querySelector(SELECTORS.price);
    if (priceCell) priceCell.textContent = formatMoney(price);

    // Update thumbnail
    if (image) {
      var thumb = row.querySelector(SELECTORS.thumb);
      if (thumb && thumb.tagName === 'IMG') {
        thumb.src = image;
      }
    }

    // Update availability
    var qtyInput = row.querySelector(SELECTORS.qty);
    if (qtyInput) {
      qtyInput.disabled = !available;
      if (!available) {
        qtyInput.value = '0';
      }
    }

    // Toggle sold out class
    row.classList.toggle('qo__row--sold-out', !available);

    // Recalculate line total + stepper state
    updateStepperButtons(row);
    updateLineTotal(row);
    updateGrandTotal();
  }

  // -----------------------------------------------------------------------
  // Quantity Change
  // -----------------------------------------------------------------------

  function handleQuantityChange(row) {
    var qtyInput = row.querySelector(SELECTORS.qty);
    if (!qtyInput) return;

    var val = parseInt(qtyInput.value, 10);
    if (isNaN(val) || val < 0) {
      val = 0;
      qtyInput.value = '0';
    }
    if (val > 99) {
      val = 99;
      qtyInput.value = '99';
    }

    row.classList.toggle('qo__row--active', val > 0);
    updateStepperButtons(row, val);
    updateLineTotal(row);
    updateGrandTotal();
  }

  function updateStepperButtons(row, val) {
    var qtyInput = row.querySelector(SELECTORS.qty);
    if (!qtyInput) return;
    if (typeof val !== 'number') {
      val = parseInt(qtyInput.value, 10) || 0;
    }
    var disabledRow = qtyInput.disabled;
    var minus = row.querySelector(SELECTORS.qtyDecrement);
    var plus = row.querySelector(SELECTORS.qtyIncrement);
    if (minus) minus.disabled = disabledRow || val <= 0;
    if (plus) plus.disabled = disabledRow || val >= 99;
  }

  function adjustQty(row, delta) {
    var qtyInput = row.querySelector(SELECTORS.qty);
    if (!qtyInput || qtyInput.disabled) return;
    var val = parseInt(qtyInput.value, 10) || 0;
    qtyInput.value = String(Math.max(0, Math.min(99, val + delta)));
    handleQuantityChange(row);
  }

  // -----------------------------------------------------------------------
  // Line Total
  // -----------------------------------------------------------------------

  function updateLineTotal(row) {
    var qtyInput = row.querySelector(SELECTORS.qty);
    var totalCell = row.querySelector(SELECTORS.lineTotal);
    if (!qtyInput || !totalCell) return;

    var qty = parseInt(qtyInput.value, 10) || 0;
    var price = parseInt(row.getAttribute('data-price'), 10) || 0;

    if (qty > 0) {
      totalCell.textContent = formatMoney(price * qty);
    } else {
      totalCell.textContent = '\u2014'; // em dash
    }
  }

  // -----------------------------------------------------------------------
  // Grand Total
  // -----------------------------------------------------------------------

  function updateGrandTotal() {
    var totalCents = 0;
    var totalItems = 0;

    allRows.forEach(function (row) {
      var qtyInput = row.querySelector(SELECTORS.qty);
      if (!qtyInput) return;
      var qty = parseInt(qtyInput.value, 10) || 0;
      if (qty > 0) {
        var price = parseInt(row.getAttribute('data-price'), 10) || 0;
        totalCents += price * qty;
        totalItems += qty;
      }
    });

    footerCount.textContent = totalItems + (totalItems === 1 ? ' item' : ' items');
    footerTotal.textContent = formatMoney(totalCents);
    submitBtn.disabled = totalItems === 0;

    // Announce to screen readers
    announce(totalItems + ' items, ' + formatMoney(totalCents));
  }

  // -----------------------------------------------------------------------
  // Add to Cart
  // -----------------------------------------------------------------------

  function getItemsToAdd() {
    var items = [];
    allRows.forEach(function (row) {
      if (row.hidden) return;
      var qtyInput = row.querySelector(SELECTORS.qty);
      if (!qtyInput) return;
      var qty = parseInt(qtyInput.value, 10) || 0;
      if (qty > 0 && row.getAttribute('data-available') === 'true') {
        items.push({
          id: parseInt(row.getAttribute('data-variant-id'), 10),
          quantity: qty,
          row: row
        });
      }
    });
    return items;
  }

  function handleAddToCart() {
    var items = getItemsToAdd();
    if (items.length === 0) return;

    hideBanners();
    setSubmitLoading(true);

    var payload = {
      items: items.map(function (item) {
        return { id: item.id, quantity: item.quantity };
      })
    };

    fetch((window.theme && window.theme.routes && window.theme.routes.cart
      ? window.theme.routes.cart.add
      : '/cart/add') + '.js', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (data.status && data.status === 422) {
          // Batch failed — fall back to sequential adds
          return addItemsSequentially(items);
        }
        return { added: items.length, failed: 0, failedRows: [] };
      })
      .then(function (result) {
        return refreshCart().then(function () { return result; });
      })
      .then(function (result) {
        setSubmitLoading(false);

        if (result.failed > 0) {
          showError(result.added + ' added, ' + result.failed + ' could not be added. Check highlighted rows.');
          result.failedRows.forEach(function (row) {
            row.classList.add('qo__row--error');
          });
        } else {
          showSuccess(result.added + ' item' + (result.added !== 1 ? 's' : '') + ' added to cart.');
          resetAllQuantities();
        }
      })
      .catch(function (err) {
        setSubmitLoading(false);
        if (err && err.message && err.message.indexOf('401') !== -1) {
          window.location.href = '/account/login?return_url=' + encodeURIComponent(window.location.pathname);
          return;
        }
        showError('Something went wrong. Your quantities have been preserved — please try again.');
      });
  }

  function addItemsSequentially(items) {
    var added = 0;
    var failed = 0;
    var failedRows = [];
    var addUrl = (window.theme && window.theme.routes && window.theme.routes.cart
      ? window.theme.routes.cart.add
      : '/cart/add') + '.js';

    // Process items sequentially with reduce
    return items.reduce(function (promise, item) {
      return promise.then(function () {
        return fetch(addUrl, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: item.id, quantity: item.quantity }] })
        })
          .then(function (res) { return res.json(); })
          .then(function (data) {
            if (data.status && data.status === 422) {
              failed++;
              failedRows.push(item.row);
              item.row.querySelector('[data-qo-qty]').parentElement.setAttribute(
                'data-error', data.description || 'Could not add'
              );
            } else {
              added++;
            }
          });
      });
    }, Promise.resolve()).then(function () {
      return { added: added, failed: failed, failedRows: failedRows };
    });
  }

  function refreshCart() {
    var cartUrl = (window.theme && window.theme.routes && window.theme.routes.cart
      ? window.theme.routes.cart.base
      : '/cart') + '.js';

    return fetch(cartUrl, { method: 'GET', credentials: 'include' })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        // Dispatch theme events so cart drawer / header count update
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
        document.dispatchEvent(new CustomEvent('quick-cart:updated'));
      })
      .catch(function () {
        // Silently fail — cart will refresh on next navigation
      });
  }

  // -----------------------------------------------------------------------
  // Reset
  // -----------------------------------------------------------------------

  function resetAllQuantities() {
    allRows.forEach(function (row) {
      var qtyInput = row.querySelector(SELECTORS.qty);
      if (qtyInput) {
        qtyInput.value = '0';
      }
      row.classList.remove('qo__row--active', 'qo__row--error');
      var totalCell = row.querySelector(SELECTORS.lineTotal);
      if (totalCell) totalCell.textContent = '\u2014';
      // Clear error data attributes
      var cells = row.querySelectorAll('[data-error]');
      cells.forEach(function (cell) { cell.removeAttribute('data-error'); });
      updateStepperButtons(row, 0);
    });
    updateGrandTotal();
  }

  // -----------------------------------------------------------------------
  // Banners
  // -----------------------------------------------------------------------

  function showError(msg) {
    errorBanner.textContent = msg;
    errorBanner.setAttribute('aria-hidden', 'false');
    errorBanner.focus();
  }

  var successDismissTimer = null;

  function showSuccess(msg) {
    successBanner.textContent = msg;
    successBanner.setAttribute('aria-hidden', 'false');
    clearTimeout(successDismissTimer);
    successDismissTimer = setTimeout(function () {
      successBanner.setAttribute('aria-hidden', 'true');
      successBanner.textContent = '';
    }, 5000);
  }

  function hideBanners() {
    clearTimeout(successDismissTimer);
    errorBanner.setAttribute('aria-hidden', 'true');
    errorBanner.textContent = '';
    successBanner.setAttribute('aria-hidden', 'true');
    successBanner.textContent = '';
    // Clear row errors
    section.querySelectorAll('.qo__row--error').forEach(function (row) {
      row.classList.remove('qo__row--error');
    });
    section.querySelectorAll('[data-error]').forEach(function (el) {
      el.removeAttribute('data-error');
    });
  }

  function setSubmitLoading(loading) {
    submitBtn.classList.toggle('qo__submit-btn--loading', loading);
    submitBtn.disabled = loading;
  }

  // -----------------------------------------------------------------------
  // Accessibility
  // -----------------------------------------------------------------------

  var announceDebounce = null;

  function announce(msg) {
    clearTimeout(announceDebounce);
    announceDebounce = setTimeout(function () {
      liveRegion.textContent = msg;
    }, 300);
  }

  // -----------------------------------------------------------------------
  // Keyboard Navigation
  // -----------------------------------------------------------------------

  function handleKeydown(e) {
    var target = e.target;

    // Enter on qty input — move to next row's qty input
    if (e.key === 'Enter' && target.matches(SELECTORS.qty)) {
      e.preventDefault();
      var allQtyInputs = Array.from(section.querySelectorAll(SELECTORS.qty + ':not(:disabled)'));
      var currentIndex = allQtyInputs.indexOf(target);
      if (currentIndex < allQtyInputs.length - 1) {
        allQtyInputs[currentIndex + 1].focus();
        allQtyInputs[currentIndex + 1].select();
      }
    }
  }

  // -----------------------------------------------------------------------
  // Event Delegation
  // -----------------------------------------------------------------------

  section.addEventListener('change', function (e) {
    var target = e.target;
    if (target.matches(SELECTORS.variantSelect)) {
      handleVariantChange(target.closest(SELECTORS.row));
    }
  });

  section.addEventListener('input', function (e) {
    var target = e.target;
    if (target.matches(SELECTORS.qty)) {
      handleQuantityChange(target.closest(SELECTORS.row));
    }
  });

  section.addEventListener('click', function (e) {
    var target = e.target;
    if (target.closest(SELECTORS.submitBtn)) {
      handleAddToCart();
      return;
    }
    if (target.closest(SELECTORS.clearBtn)) {
      resetAllQuantities();
      return;
    }
    var minus = target.closest(SELECTORS.qtyDecrement);
    if (minus) {
      adjustQty(minus.closest(SELECTORS.row), -1);
      return;
    }
    var plus = target.closest(SELECTORS.qtyIncrement);
    if (plus) {
      adjustQty(plus.closest(SELECTORS.row), 1);
      return;
    }
  });

  section.addEventListener('keydown', handleKeydown);

  // -----------------------------------------------------------------------
  // Init: set tabindex on banners for focus management
  // -----------------------------------------------------------------------

  errorBanner.setAttribute('tabindex', '-1');
  successBanner.setAttribute('tabindex', '-1');

})();
